import { MemoryFileSystem, ZipFileSystem, ZipReadFileSystem, type FileSystem } from '@playcanvas/splat-transform';
import { Color, Vec3, Quat } from 'playcanvas';

import type { Annotation, AnnotationLayer } from '../annotations';
import type { Bookmark } from '../bookmarks';
import { BoxShape } from '../box-shape';
import type { Pose } from '../camera-poses';
import { Element, ElementType } from '../element';
import { BlobReadSource, MappedReadFileSystem } from '../io';
import type { Measurement } from '../measurements';
import { Mesh } from '../mesh';
import type { ReconstructionTimeline } from '../reconstruction-timeline';
import { Scene } from '../scene';
import type { SceneCollisionExport } from '../scene-collision';
import { ShadowLight } from '../shadow-light';
import { SphereShape } from '../sphere-shape';
import { Splat } from '../splat';
import { serializeSog, serializeSplat } from '../splat-serialize';
import type { Tour, TourStep } from '../tours';

type NsvElement =
    | {
          type: 'splat';
          name: string;
          path: string;
          transform: { t: number[]; r: number[]; s: number[] };
          visible?: boolean;
          pivotOffset?: [number, number, number];
      }
    | {
          type: 'mesh';
          name: string;
          path: string;
          transform: { t: number[]; r: number[]; s: number[] };
          renderVisible?: boolean;
          pivotOffset?: [number, number, number];
          billboard?: boolean;
          videoPlaybackMode?: 'realtime' | 'per-frame';
          shadowCatcher?: boolean;
          collision?: {
              mode: 'none' | 'block';
              shape: 'bounds' | 'primitive';
              debug?: boolean;
          };
          materials?: Array<{
              meshInstanceIndex: number;
              diffuseTexturePath?: string;
              videoTexturePath?: string;
              useAlpha?: boolean;
          }>;
      }
    | {
          type: 'shadowLight';
          name: string;
          transform: { t: number[]; r: number[]; s: number[] };
          enabled?: boolean;
          intensity?: number;
          color?: number[];
          castShadows?: boolean;
          shadowDistance?: number;
          shadowResolution?: number;
      }
    | {
          type: 'collider';
          shape: 'box' | 'sphere';
          name?: string;
          transform: { t: number[]; r: number[]; s: number[] };
          params: { halfExtents?: number[]; radius?: number };
          debugVisible?: boolean;
      };

export type CaseMetadata = {
    caseType?: 'accident' | 'premises' | 'crime-scene' | 'construction' | 'general';
    caseNumber?: string;
    caseName?: string;
    jurisdiction?: string;
    incidentDate?: string;
    scanDate?: string;
    preparedBy?: string;
    disclaimers?: string[];
};

const cloneAnnotation = (annotation: Annotation): Annotation => {
    return {
        ...annotation,
        worldPosition: [annotation.worldPosition[0], annotation.worldPosition[1], annotation.worldPosition[2]],
        worldNormal: annotation.worldNormal ? [annotation.worldNormal[0], annotation.worldNormal[1], annotation.worldNormal[2]] : undefined,
        linkedEvidenceIds: annotation.linkedEvidenceIds?.slice()
    };
};

const cloneAnnotationLayer = (layer: AnnotationLayer): AnnotationLayer => {
    return {
        ...layer,
        color: layer.color ? [layer.color[0], layer.color[1], layer.color[2]] : undefined
    };
};

const cloneBookmark = (bookmark: Bookmark): Bookmark => {
    return {
        ...bookmark,
        cameraPose: {
            ...bookmark.cameraPose,
            position: [bookmark.cameraPose.position[0], bookmark.cameraPose.position[1], bookmark.cameraPose.position[2]],
            target: [bookmark.cameraPose.target[0], bookmark.cameraPose.target[1], bookmark.cameraPose.target[2]]
        },
        visibleLayerIds: bookmark.visibleLayerIds?.slice()
    };
};

const cloneMeasurement = (measurement: Measurement): Measurement => {
    return {
        ...measurement,
        points: measurement.points.map(point => [point[0], point[1], point[2]]),
        a: measurement.a ? [measurement.a[0], measurement.a[1], measurement.a[2]] : undefined,
        b: measurement.b ? [measurement.b[0], measurement.b[1], measurement.b[2]] : undefined
    };
};

const cloneTourStep = (step: TourStep): TourStep => {
    const base = {
        transitionDuration: step.transitionDuration,
        pauseDuration: step.pauseDuration,
        layerStateOverride: step.layerStateOverride ? { ...step.layerStateOverride } : undefined,
        label: step.label,
        narrationNote: step.narrationNote
    };

    if (step.type === 'bookmark') {
        return {
            ...base,
            type: 'bookmark',
            bookmarkId: step.bookmarkId
        };
    }

    return {
        ...base,
        type: 'customView',
        cameraPose: {
            position: [step.cameraPose.position[0], step.cameraPose.position[1], step.cameraPose.position[2]],
            target: [step.cameraPose.target[0], step.cameraPose.target[1], step.cameraPose.target[2]],
            fov: step.cameraPose.fov
        }
    };
};

const cloneTour = (tour: Tour): Tour => {
    return {
        ...tour,
        steps: tour.steps.map(cloneTourStep)
    };
};

type NsvCameraPose = {
    name: string;
    frame: number;
    position: number[];
    target: number[];
    source?: string;
    validated?: boolean;
    exhibitId?: string;
    capturedAt?: string;
    capturedBy?: string;
    device?: string;
    tags?: string[];
    description?: string;
};

const cloneCameraPose = (pose: Pose): NsvCameraPose => {
    return {
        name: pose.name,
        frame: pose.frame,
        position: [pose.position.x, pose.position.y, pose.position.z],
        target: [pose.target.x, pose.target.y, pose.target.z],
        source: pose.source,
        validated: pose.validated,
        exhibitId: pose.exhibitId,
        capturedAt: pose.capturedAt,
        capturedBy: pose.capturedBy,
        device: pose.device,
        tags: pose.tags?.slice(),
        description: pose.description
    };
};

export type NsvManifest = {
    version: number;
    unitMode?: 'metric' | 'imperial';
    speedWidgets?: Array<{ id: string; targetRef: string | null }>;
    meta?: {
        title?: string;
        author?: string;
        createdAt?: string;
        appVersion?: string;
    };
    scene?: {
        worldBound?: { center: number[]; halfExtents: number[] };
    };
    view?: {
        showGrid?: boolean;
        showBound?: boolean;
        outlineSelection?: boolean;
        shBands?: number;
        centersSize?: number;
        measureSplatPickMode?: 'normal' | 'center' | 'none';
        measureMinSplatOpacity?: number;
        clipBox?: {
            enabled: boolean;
            center: number[];
            halfExtents: number[];
            rotation: number[];
        };
        // NOTE: 'occluder' is deprecated/disabled; kept only for backward compatibility.
        clipBoxMode?: 'shader' | 'test' | 'occluder';
    };
    camera?: {
        focalPoint?: number[];
        azim?: number;
        elev?: number;
        distance?: number;
        fov?: number;
        controlMode?: 'orbit' | 'fly' | 'walk';
        flySpeed?: number;
        flyVerticalMode?: 'free' | 'keyboard';
    };
    collision?: {
        kind: 'voxel' | 'mesh';
        name?: string;
        files: {
            json?: string;
            bin?: string;
            mesh?: string;
        };
    };
    cameraPoses?: NsvCameraPose[];
    caseMetadata?: CaseMetadata;
    reconstructionTimeline?: ReconstructionTimeline;
    annotations?: Annotation[];
    annotationLayers?: AnnotationLayer[];
    bookmarks?: Bookmark[];
    tours?: Tour[];
    measurements?: Measurement[];
    measurementNextNumber?: number;
    elements: NsvElement[];
};

const clearCaseMetadataPatch: CaseMetadata = {
    caseType: undefined,
    caseNumber: undefined,
    caseName: undefined,
    jurisdiction: undefined,
    incidentDate: undefined,
    scanDate: undefined,
    preparedBy: undefined,
    disclaimers: undefined
};

/**
 * Export selected (or all) scene content to a ZIP-based .nsite bundle.
 * @param scene - The active scene instance
 * @param fs - Destination filesystem
 * @param selection - Optional subset of elements to export
 */
export async function writeNsvBundle(scene: Scene, fs: FileSystem, selection?: Element[], options?: { useSog?: boolean, progress?: (text: string) => void }) {
    const zipWriter = await fs.createWriter('output.nsite');
    const zip = new ZipFileSystem(zipWriter);

    const writeZipFile = async (filename: string, data: string | Uint8Array) => {
        const writer = await zip.createWriter(filename);
        if (typeof data === 'string') {
            await writer.write(new TextEncoder().encode(data));
        } else {
            await writer.write(data);
        }
        await writer.close();
    };
    const useSog = options?.useSog ?? true;
    const progress = options?.progress;
    const manifest: NsvManifest = {
        version: 2,
        unitMode: (() => {
            // Avoid invoking missing functions (would log to console).
            if (!(scene.events as any)?.functions?.has?.('units.mode')) {
                return undefined;
            }
            const mode = scene.events.invoke('units.mode');
            return (mode === 'metric' || mode === 'imperial') ? mode : undefined;
        })(),
        speedWidgets: (() => {
            // Optional C.7: speed HUD widget persistence.
            try {
                // Avoid invoking missing functions (would log to console).
                if (!(scene.events as any)?.functions?.has?.('speedhud.widgets')) {
                    return undefined;
                }
                const widgets = scene.events.invoke('speedhud.widgets') as any;
                if (!Array.isArray(widgets)) {
                    return undefined;
                }
                return widgets
                .map((w: any) => {
                    const id = String(w?.id ?? '').trim();
                    if (!id) return null;
                    const targetRefRaw = w?.targetRef;
                    const targetRef = (typeof targetRefRaw === 'string' && targetRefRaw.trim()) ? targetRefRaw.trim() : null;
                    return { id, targetRef };
                })
                .filter(Boolean) as Array<{ id: string; targetRef: string | null }>;
            } catch {
                return undefined;
            }
        })(),
        meta: {
            title: document.title,
            author: undefined,
            createdAt: new Date().toISOString(),
            appVersion: undefined
        },
        scene: {
            worldBound: (() => {
                const b = scene.bound;
                const c = b.center;
                const he = b.halfExtents;
                return {
                    center: [c.x, c.y, c.z],
                    halfExtents: [he.x, he.y, he.z]
                };
            })()
        },
        view: {
            showGrid: !!scene.events.invoke('grid.visible'),
            showBound: !!scene.events.invoke('camera.bound'),
            outlineSelection: !!scene.events.invoke('view.outlineSelection'),
            shBands: scene.events.invoke('view.bands') as number,
            centersSize: scene.events.invoke('camera.splatSize') as number,
            measureSplatPickMode: scene.events.invoke('measure.splatPickMode') as any,
            measureMinSplatOpacity: scene.events.invoke('measure.minSplatOpacity') as any,
            clipBox: (() => {
                const cb: any = scene.events.invoke('clipBox');
                if (!cb) return undefined;
                return {
                    enabled: !!cb.enabled,
                    center: [cb.center.x, cb.center.y, cb.center.z],
                    halfExtents: [cb.halfExtents.x, cb.halfExtents.y, cb.halfExtents.z],
                    rotation: [cb.rotation.x, cb.rotation.y, cb.rotation.z, cb.rotation.w]
                };
            })(),
            clipBoxMode: scene.events.invoke('clipBox.mode') as any
        },
        camera: {
            focalPoint: (() => {
                const t = scene.camera.focalPoint;
                return [t.x, t.y, t.z];
            })(),
            azim: scene.camera.azim,
            elev: scene.camera.elevation,
            distance: scene.camera.distance,
            fov: scene.camera.fov,
            controlMode: scene.events.invoke('camera.controlMode') as any,
            flySpeed: scene.events.invoke('camera.flySpeed') as any,
            flyVerticalMode: scene.events.invoke('camera.flyVerticalMode') as any
        },
        cameraPoses: (() => {
            const poses = scene.events.invoke('camera.poses') as Pose[] | undefined;
            if (!Array.isArray(poses)) {
                return undefined;
            }
            return poses.map(cloneCameraPose);
        })(),
        caseMetadata: (() => {
            const metadata = scene.events.invoke('case.metadata') as CaseMetadata | undefined;
            if (!metadata) {
                return undefined;
            }
            return {
                ...metadata,
                disclaimers: metadata.disclaimers?.slice()
            };
        })(),
        reconstructionTimeline: (() => {
            const timeline = scene.events.invoke('reconstructionTimeline.get') as ReconstructionTimeline | undefined;
            if (!timeline || typeof timeline !== 'object') {
                return undefined;
            }
            return timeline;
        })(),
        annotations: (() => {
            const items = scene.events.invoke('annotations.list') as Annotation[] | undefined;
            if (!Array.isArray(items)) {
                return undefined;
            }
            return items.map(cloneAnnotation);
        })(),
        annotationLayers: (() => {
            const items = scene.events.invoke('annotations.layers') as AnnotationLayer[] | undefined;
            if (!Array.isArray(items)) {
                return undefined;
            }
            return items.map(cloneAnnotationLayer);
        })(),
        bookmarks: (() => {
            const items = scene.events.invoke('bookmarks.list') as Bookmark[] | undefined;
            if (!Array.isArray(items)) {
                return undefined;
            }
            return items.map(cloneBookmark);
        })(),
        tours: (() => {
            const items = scene.events.invoke('tours.list') as Tour[] | undefined;
            if (!Array.isArray(items)) {
                return undefined;
            }
            return items.map(cloneTour);
        })(),
        measurements: (() => {
            const items = scene.events.invoke('measurements.list') as Measurement[] | undefined;
            if (!Array.isArray(items)) {
                return undefined;
            }
            return items.map(cloneMeasurement);
        })(),
        measurementNextNumber: (() => {
            const value = scene.events.invoke('measurements.nextNumber');
            return typeof value === 'number' && Number.isFinite(value) ? Math.floor(value) : undefined;
        })(),
        elements: []
    };

    const elements = (selection && selection.length ? selection : scene.elements).slice();

    const packTRS = (e: { getLocalPosition: () => Vec3; getLocalRotation: () => Quat; getLocalScale: () => Vec3 }) => {
        const p = e.getLocalPosition();
        const r = e.getLocalRotation();
        const s = e.getLocalScale();
        return {
            t: [p.x, p.y, p.z],
            r: [r.x, r.y, r.z, r.w],
            s: [s.x, s.y, s.z]
        };
    };

    const packPivotOffset = (element: any): [number, number, number] | undefined => {
        const o = element?.pivotOffset as Vec3 | undefined;
        if (!o) return undefined;
        if (o.x === 0 && o.y === 0 && o.z === 0) return undefined;
        return [o.x, o.y, o.z];
    };

    // Counters to create stable asset paths
    let splatIndex = 0;
    let meshIndex = 0;

    // 1) Splats
    for (const e of elements) {
        if (e.type !== ElementType.splat) continue;
        const splat = e as Splat;
        const currentSplatIndex = splatIndex++;

        progress?.(`Bundling splat '${splat.name}' (${useSog ? 'SOG' : 'SPLAT'})...`);

        let filename: string;
        let payload: Uint8Array;

        const memFs = new MemoryFileSystem();

        if (useSog) {
            // Serialize this splat to .sog in-memory (ZIP of meta.json + webp textures)
            await serializeSog([splat], { maxSHBands: 3, iterations: 10 }, memFs);
            payload = memFs.results.get('output.sog');
            filename = `assets/splats/splat_${currentSplatIndex}.sog`;
        } else {
            // Faster, larger: raw .splat
            await serializeSplat([splat], {}, memFs);
            payload = memFs.results.get('output.splat');
            filename = `assets/splats/splat_${currentSplatIndex}.splat`;
        }

        if (!payload) {
            throw new Error('Failed to serialize splat payload for NSite bundle');
        }

        await writeZipFile(filename, payload);

        manifest.elements.push({
            type: 'splat',
            name: splat.name,
            path: filename,
            transform: packTRS(splat.entity),
            visible: splat.visible,
            pivotOffset: packPivotOffset(splat)
        });
    }

    // 2) Meshes (container GLB/GLTF copied if original URL available)
    for (const e of elements) {
        if (e.type !== ElementType.model) continue;
        const mesh = e as Mesh;

        progress?.(`Bundling mesh '${mesh.name}'...`);

        // Best-effort: try to fetch the original container file (glb/gltf)
        let filename = `assets/meshes/mesh_${meshIndex++}.glb`;
        try {
            // Preferred: use the preserved source file from import (works even if blob URL was revoked).
            const srcFile: File | Blob | undefined = (mesh as any)._sourceMeshFile;
            const srcNameHint: string | undefined = (mesh as any)._sourceMeshName;

            const fileInfo: any = (mesh.asset as any).file;
            const srcUrl: string = fileInfo?.url;
            const srcName: string = fileInfo?.filename;
            const nameForExt = srcNameHint || srcName;
            if (nameForExt) {
                // Keep original extension if present
                if (nameForExt.toLowerCase().endsWith('.gltf') || nameForExt.toLowerCase().endsWith('.glb') || nameForExt.toLowerCase().endsWith('.obj')) {
                    filename = `assets/meshes/${sanitizeFilename(nameForExt)}`;
                } else {
                    filename = `assets/meshes/${sanitizeFilename(nameForExt)}.glb`;
                }
            }
            if (srcFile) {
                const buf = new Uint8Array(await srcFile.arrayBuffer());
                await writeZipFile(filename, buf);
            } else if (srcUrl) {
                const resp = await fetch(srcUrl);
                const buf = new Uint8Array(await resp.arrayBuffer());
                await writeZipFile(filename, buf);
            }
        } catch (err) {
            // If fetch fails, skip embedding the binary (manifest still includes the entry)
            // Consumer should handle missing file paths gracefully.
        }

        // If a texture was applied in-editor (typically OBJ workflow), embed it and
        // record a binding so re-import can re-link it.
        const materialBindings: Array<{ meshInstanceIndex: number; diffuseTexturePath?: string; videoTexturePath?: string; useAlpha?: boolean }> = [];
        try {
            const texFile: File | undefined = (mesh as any)._sourceTextureFile;
            const texName: string | undefined = (mesh as any)._sourceTextureName;
            const videoFile: File | undefined = (mesh as any)._sourceVideoFile;
            const miCount = (mesh.entity?.model?.meshInstances?.length ?? 0) as number;
            if (videoFile && mesh.videoSource) {
                const safeName = sanitizeFilename(videoFile.name || `mesh_${meshIndex}_video.webm`);
                const videoPath = `assets/videos/${safeName}`;
                if (videoFile.size > 50 * 1024 * 1024) {
                    console.warn(`[NSV] Video texture '${videoFile.name}' is ${(videoFile.size / (1024 * 1024)).toFixed(1)}MB and may significantly increase bundle size.`);
                }
                const buf = new Uint8Array(await videoFile.arrayBuffer());
                await writeZipFile(videoPath, buf);

                for (let i = 0; i < miCount; i++) {
                    materialBindings.push({ meshInstanceIndex: i, videoTexturePath: videoPath, useAlpha: mesh.useAlpha });
                }
            } else if (texFile) {
                const safeName = sanitizeFilename(texName || texFile.name || `mesh_${meshIndex}_diffuse.png`);
                const texPath = `assets/textures/${safeName}`;
                const buf = new Uint8Array(await texFile.arrayBuffer());
                await writeZipFile(texPath, buf);

                // Apply to all mesh instances for now.
                for (let i = 0; i < miCount; i++) {
                    materialBindings.push({ meshInstanceIndex: i, diffuseTexturePath: texPath, useAlpha: mesh.useAlpha || undefined });
                }
            } else if (mesh.useAlpha && miCount > 0) {
                for (let i = 0; i < miCount; i++) {
                    materialBindings.push({ meshInstanceIndex: i, useAlpha: true });
                }
            }
        } catch (e) {
            // best-effort
        }

        manifest.elements.push({
            type: 'mesh',
            name: mesh.name,
            path: filename,
            transform: packTRS(mesh.entity),
            renderVisible: (mesh as any).renderVisible ?? true,
            pivotOffset: packPivotOffset(mesh),
            billboard: (mesh as any).billboard ? true : undefined,
            videoPlaybackMode: mesh.videoSource && (mesh as any).videoPlaybackMode !== 'realtime' ? (mesh as any).videoPlaybackMode : undefined,
            shadowCatcher: (mesh as any).shadowCatcher ? true : undefined,
            collision: {
                mode: (mesh as any).collisionMode ?? 'none',
                shape: (mesh as any).collisionShape ?? 'bounds',
                debug: (mesh as any).debugCollider ?? false
            },
            materials: materialBindings.length ? materialBindings : undefined
        });
    }

    // 2.5) Shadow Light(s)
    for (const e of elements) {
        if (e.type !== ElementType.shadow) continue;
        if (!(e instanceof ShadowLight)) continue;
        const light = e as ShadowLight;

        manifest.elements.push({
            type: 'shadowLight',
            name: light.name,
            transform: packTRS(light.entity),
            enabled: light.enabled,
            intensity: light.intensity,
            color: [light.color.r, light.color.g, light.color.b],
            castShadows: light.castShadows,
            shadowDistance: light.shadowDistance,
            shadowResolution: light.shadowResolution
        });
    }

    // 3) Primitive Colliders
    for (const e of elements) {
        if (e.type !== ElementType.debug) continue;
        const dbg: any = e as any;
        if (!dbg.collider) continue;

        if (e instanceof BoxShape) {
            manifest.elements.push({
                type: 'collider',
                shape: 'box',
                name: 'BoxCollider',
                transform: packTRS(dbg.pivot),
                params: {
                    halfExtents: [dbg.lenX * 0.5, dbg.lenY * 0.5, dbg.lenZ * 0.5]
                },
                debugVisible: false
            });
        } else if (e instanceof SphereShape) {
            manifest.elements.push({
                type: 'collider',
                shape: 'sphere',
                name: 'SphereCollider',
                transform: packTRS(dbg.pivot),
                params: {
                    radius: dbg.radius
                },
                debugVisible: false
            });
        }
    }

    // Scene-level walk/collision sidecar. This replaces the earlier per-mesh
    // walk experiment and is intentionally independent of visible mesh content.
    const collisionExport = scene.events.invoke('scene.collision.export') as SceneCollisionExport | null | undefined;
    if (collisionExport) {
        progress?.(`Bundling ${collisionExport.kind} collision sidecar...`);

        const writeCollisionBlob = async (source: { path: string; blob: Blob } | undefined, fallbackName: string) => {
            if (!source) return undefined;
            const filename = `assets/collision/${sanitizeFilename(source.path || fallbackName)}`;
            const buf = new Uint8Array(await source.blob.arrayBuffer());
            await writeZipFile(filename, buf);
            return filename;
        };

        const jsonPath = await writeCollisionBlob(collisionExport.files.json, 'collision.voxel.json');
        const binPath = await writeCollisionBlob(collisionExport.files.bin, 'collision.voxel.bin');
        const meshPath = await writeCollisionBlob(collisionExport.files.mesh, 'collision.collision.glb');

        manifest.collision = {
            kind: collisionExport.kind,
            name: collisionExport.name,
            files: {
                json: jsonPath,
                bin: binPath,
                mesh: meshPath
            }
        };
    }

    // 4) Write manifest
    progress?.('Writing manifest.json...');
    await writeZipFile('manifest.json', JSON.stringify(manifest, null, 2));

    // Finish
    progress?.('Finalizing bundle...');
    await zip.close();
}

function sanitizeFilename(name: string) {
    return name.replace(/[\\/:*?"<>|]/g, '_');
}

function getMimeTypeFromPath(path: string, fallback = 'application/octet-stream') {
    const lower = path.toLowerCase();
    if (lower.endsWith('.webm')) return 'video/webm';
    if (lower.endsWith('.mp4')) return 'video/mp4';
    if (lower.endsWith('.png')) return 'image/png';
    if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
    if (lower.endsWith('.webp')) return 'image/webp';
    return fallback;
}

/**
 * Import an .nsite bundle (ZIP) from an ArrayBuffer and attach its contents to the scene.
 * @param buffer - The .nsite ZIP data
 * @param scene - The scene to populate
 */
export async function readNsvBundle(buffer: ArrayBuffer, scene: Scene): Promise<void> {
    // If the caller passes an empty/unfinished buffer (e.g. selecting the file that is
    // currently being written), the ZIP reader can throw a confusing corruption error.
    if (!buffer || buffer.byteLength < 4) {
        throw new Error(`Invalid NSV bundle: empty buffer (size=${buffer?.byteLength ?? 0}). The file may still be writing.`);
    }

    const readSourceBytes = async (source: any): Promise<Uint8Array> => {
        const stream = source.read();
        const chunks: Uint8Array[] = [];
        let total = 0;
        const scratch = new Uint8Array(1024 * 1024);

        while (true) {
            const read = await stream.pull(scratch);
            if (read <= 0) break;
            const chunk = new Uint8Array(read);
            chunk.set(scratch.subarray(0, read));
            chunks.push(chunk);
            total += read;
        }

        source.close?.();

        const out = new Uint8Array(total);
        let offset = 0;
        for (const c of chunks) {
            out.set(c, offset);
            offset += c.length;
        }
        return out;
    };

    const zipSource = new BlobReadSource(new Blob([buffer], { type: 'application/zip' }));
    const zipFs = new ZipReadFileSystem(zipSource);

    const readZipText = async (filename: string) => {
        const source = await zipFs.createSource(filename);
        const bytes = await readSourceBytes(source);
        return new TextDecoder().decode(bytes);
    };

    const readZipBlob = async (filename: string, mime = 'application/octet-stream') => {
        try {
            const source = await zipFs.createSource(filename);
            const bytes = await readSourceBytes(source);
            const blobBytes = new Uint8Array(bytes.byteLength);
            blobBytes.set(bytes);
            return new Blob([blobBytes], { type: mime });
        } catch {
            return null;
        }
    };

    try {
        const manifestText = await readZipText('manifest.json');
        const manifest: NsvManifest = JSON.parse(manifestText);

        // Apply unit mode as early as possible (best-effort)
        try {
            const mode = (manifest as any).unitMode;
            if (mode === 'metric' || mode === 'imperial') {
                scene.events.fire('units.setMode', mode);
            }
        } catch {
            // ignore
        }

        // Validate NSV version to prevent processing unsupported formats
        const version = (manifest as any).version || 2; // Default to v2 if not specified
        const SUPPORTED_VERSIONS = [1, 2];
        if (!SUPPORTED_VERSIONS.includes(version)) {
            throw new Error(`Unsupported NSV version: ${version} (supported: ${SUPPORTED_VERSIONS.join(', ')})`);
        }

        // Basic NSV v1 compatibility:
        // - v1 splats were stored as `.splat`
        // - v2 splats are stored as `.sog`
        // Apply view/UI settings first so imported content renders consistently.
        try {
            scene.events.fire('case.setMetadata', clearCaseMetadataPatch);
            if (manifest.caseMetadata && typeof manifest.caseMetadata === 'object') {
                scene.events.fire('case.setMetadata', manifest.caseMetadata);
            }
            scene.events.fire('reconstructionTimeline.clear');
            if (manifest.reconstructionTimeline && typeof manifest.reconstructionTimeline === 'object') {
                scene.events.fire('reconstructionTimeline.set', manifest.reconstructionTimeline);
            }
            scene.events.fire('annotations.setAll', Array.isArray(manifest.annotations) ? manifest.annotations : []);
            scene.events.fire('annotations.layers.setAll', Array.isArray(manifest.annotationLayers) ? manifest.annotationLayers : []);
            scene.events.fire('bookmarks.setAll', Array.isArray(manifest.bookmarks) ? manifest.bookmarks : []);
            scene.events.fire('tours.setAll', Array.isArray(manifest.tours) ? manifest.tours : []);
            scene.events.fire('measurements.restoreState', {
                items: Array.isArray(manifest.measurements) ? manifest.measurements : [],
                selected: null,
                nextNumber: manifest.measurementNextNumber
            });
            scene.events.fire('camera.setPoses', Array.isArray(manifest.cameraPoses) ? manifest.cameraPoses : []);

            if (manifest.view) {
                const v: any = manifest.view;
                if (typeof v.showGrid === 'boolean') scene.events.fire('grid.setVisible', v.showGrid);
                if (typeof v.showBound === 'boolean') scene.events.fire('camera.setBound', v.showBound);
                if (typeof v.outlineSelection === 'boolean') scene.events.fire('view.setOutlineSelection', v.outlineSelection);
                if (typeof v.shBands === 'number') scene.events.fire('view.setBands', v.shBands);
                if (typeof v.centersSize === 'number') scene.events.fire('camera.setSplatSize', v.centersSize);

                // Measure settings
                if (typeof v.measureSplatPickMode === 'string') scene.events.fire('measure.setSplatPickMode', v.measureSplatPickMode);
                if (typeof v.measureMinSplatOpacity === 'number') scene.events.fire('measure.setMinSplatOpacity', v.measureMinSplatOpacity);

                // Global clip box
                if (v.clipBox) {
                    const cb = v.clipBox;
                    const rot = cb.rotation;
                    const q = Array.isArray(rot) && rot.length === 4 ? new Quat(rot[0], rot[1], rot[2], rot[3]) : new Quat(0, 0, 0, 1);
                    scene.events.fire('clipBox.set', {
                        enabled: !!cb.enabled,
                        center: new Vec3(cb.center),
                        halfExtents: new Vec3(cb.halfExtents),
                        rotation: q
                    });
                }
                if (typeof v.clipBoxMode === 'string') {
                    // Backward compatible: map old 'occluder' mode to 'test'
                    scene.events.fire('clipBox.setMode', v.clipBoxMode === 'occluder' ? 'test' : v.clipBoxMode);
                }
            }

            // Restore HUD widgets (best-effort; targets may not resolve until helpers exist).
            const widgets = (manifest as any).speedWidgets;
            if (Array.isArray(widgets)) {
                scene.events.fire('speedhud.setWidgets', widgets);
            }

        } catch (e) {
            // best effort
        }

        if (manifest.collision?.kind === 'voxel') {
            const jsonPath = manifest.collision.files?.json;
            const binPath = manifest.collision.files?.bin;
            const jsonBlob = jsonPath ? await readZipBlob(jsonPath, 'application/json') : null;
            const binBlob = binPath ? await readZipBlob(binPath, 'application/octet-stream') : null;
            if (!jsonBlob || !binBlob) {
                throw new Error('NSV bundle is incomplete: missing voxel collision sidecar');
            }
            await scene.events.invoke('scene.collision.loadBlobs', {
                kind: 'voxel',
                name: manifest.collision.name,
                jsonBlob,
                jsonName: jsonPath.split('/').pop(),
                binBlob,
                binName: binPath.split('/').pop()
            });
        } else if (manifest.collision?.kind === 'mesh') {
            const meshPath = manifest.collision.files?.mesh;
            const meshBlob = meshPath ? await readZipBlob(meshPath, 'model/gltf-binary') : null;
            if (!meshBlob) {
                throw new Error('NSV bundle is incomplete: missing mesh collision sidecar');
            }
            await scene.events.invoke('scene.collision.loadBlobs', {
                kind: 'mesh',
                name: manifest.collision.name,
                meshBlob,
                meshName: meshPath.split('/').pop()
            });
        } else {
            scene.events.fire('scene.collision.clear');
        }

        // Import elements
        for (const el of manifest.elements) {
            if (el.type === 'splat') {
                const lower = el.path.toLowerCase();
                const isSog = lower.endsWith('.sog');
                const isSplat = lower.endsWith('.splat');
                const mime = isSog ? 'application/zip' : (isSplat ? 'application/x-gaussian-splat' : 'application/octet-stream');
                const blob = await readZipBlob(el.path, mime);
                if (!blob) {
                    console.error(`NSV import error: Missing splat asset '${el.path}'`);
                    throw new Error(`NSV bundle is incomplete: missing asset '${el.path}'`);
                }
                const file = new File([blob], el.path.split('/').pop(), { type: blob.type });

                const fileSystem = new MappedReadFileSystem();
                fileSystem.addFile(file.name, file);

                const model = await scene.assetLoader.load(file.name, fileSystem);
                await scene.add(model);

                // Apply user transform
                tryApplyTRS((model as any).entity, el.transform);
                // Apply persisted pivot offset (if present)
                try {
                    const po = (el as any).pivotOffset;
                    if (Array.isArray(po) && po.length === 3 && (model as any).pivotOffset) {
                        (model as any).pivotOffset.copy(new Vec3(po[0], po[1], po[2]));
                    }
                } catch {
                    // best effort
                }
                if (typeof (model as any).visible === 'boolean' && typeof el.visible === 'boolean') {
                    (model as any).visible = el.visible;
                }
            } else if (el.type === 'mesh') {
                const blob = await readZipBlob(el.path, 'model/gltf-binary');
                if (!blob) {
                    console.error(`NSV import error: Missing mesh asset '${el.path}'`);
                    throw new Error(`NSV bundle is incomplete: missing asset '${el.path}'`);
                }
                const file = new File([blob], el.path.split('/').pop(), { type: blob.type });

                const fileSystem = new MappedReadFileSystem();
                fileSystem.addFile(file.name, file);
                const model = await scene.assetLoader.load(file.name, fileSystem);
                await scene.add(model);
                tryApplyTRS((model as any).entity, el.transform);

                // Apply persisted pivot offset (if present)
                try {
                    const po = (el as any).pivotOffset;
                    if (Array.isArray(po) && po.length === 3 && (model as any).pivotOffset) {
                        (model as any).pivotOffset.copy(new Vec3(po[0], po[1], po[2]));
                    }
                } catch {
                    // best effort
                }

                // Flags
                if (el.renderVisible !== undefined) {
                    (model as any).renderVisible = el.renderVisible;
                }
                if (typeof (el as any).billboard === 'boolean') {
                    (model as any).billboard = (el as any).billboard;
                }
                if ((el as any).videoPlaybackMode === 'per-frame' || (el as any).videoPlaybackMode === 'realtime') {
                    (model as any).videoPlaybackMode = (el as any).videoPlaybackMode;
                }
                if (typeof (el as any).shadowCatcher === 'boolean') {
                    (model as any).shadowCatcher = (el as any).shadowCatcher;
                }
                if (el.collision) {
                    (model as any).collisionMode = el.collision.mode ?? 'none';
                    (model as any).collisionShape = el.collision.shape ?? 'bounds';
                    (model as any).debugCollider = el.collision.debug ?? false;
                }

                // Re-link any embedded textures
                try {
                    const m = model as any;
                    const bindings = (el as any).materials as Array<{ meshInstanceIndex: number; diffuseTexturePath?: string; videoTexturePath?: string; useAlpha?: boolean }> | undefined;
                    const savedUseAlpha = bindings?.find((binding) => typeof binding.useAlpha === 'boolean')?.useAlpha;
                    if (bindings?.length) {
                        for (const b of bindings) {
                            if (b.videoTexturePath) {
                                const videoBlob = await readZipBlob(b.videoTexturePath, getMimeTypeFromPath(b.videoTexturePath, 'video/webm'));
                                if (!videoBlob) {
                                    console.warn('[NSV] Video blob not found for path:', b.videoTexturePath);
                                    continue;
                                }
                                const videoFile = new File([videoBlob], b.videoTexturePath.split('/').pop() ?? 'video', { type: videoBlob.type });
                                if (typeof m.loadVideoTexture === 'function') {
                                    await m.loadVideoTexture(videoFile);
                                    if (typeof savedUseAlpha === 'boolean') {
                                        m.useAlpha = savedUseAlpha;
                                    }
                                    if ((el as any).videoPlaybackMode === 'per-frame' || (el as any).videoPlaybackMode === 'realtime') {
                                        m.videoPlaybackMode = (el as any).videoPlaybackMode;
                                    }
                                    break;
                                } else {
                                    console.warn('[NSV] mesh.loadVideoTexture is not a function!');
                                }
                                continue;
                            }

                            if (!b.diffuseTexturePath) continue;
                            const texBlob = await readZipBlob(b.diffuseTexturePath, getMimeTypeFromPath(b.diffuseTexturePath, 'image/*'));
                            if (!texBlob) {
                                console.warn('[NSV] Texture blob not found for path:', b.diffuseTexturePath);
                                continue;
                            }
                            const texFile = new File([texBlob], b.diffuseTexturePath.split('/').pop() ?? 'texture', { type: texBlob.type });
                            // Current Mesh implementation applies a single diffuse texture to all meshInstances.
                            // For now, just call loadTexture once.
                            if (typeof m.loadTexture === 'function') {
                                await m.loadTexture(texFile);
                                if (typeof savedUseAlpha === 'boolean') {
                                    m.useAlpha = savedUseAlpha;
                                }
                                break;
                            } else {
                                console.warn('[NSV] mesh.loadTexture is not a function!');
                            }
                        }
                    }
                    if (typeof savedUseAlpha === 'boolean' && !m.hasTexture) {
                        m.useAlpha = savedUseAlpha;
                    }
                } catch (e) {
                    console.error('[NSV] Error loading texture:', e);
                }
            } else if (el.type === 'collider') {
                if (el.shape === 'box') {
                    const box = new BoxShape();
                    scene.add(box);
                    if (el.params?.halfExtents) {
                    // BoxShape uses full lengths internally
                        box.lenX = (el.params.halfExtents[0] ?? 0.5) * 2;
                        box.lenY = (el.params.halfExtents[1] ?? 0.5) * 2;
                        box.lenZ = (el.params.halfExtents[2] ?? 0.5) * 2;
                    }
                    box.collider = true;
                    tryApplyTRS(box.pivot, el.transform);
                } else if (el.shape === 'sphere') {
                    const sph = new SphereShape();
                    scene.add(sph);
                    if (el.params?.radius !== undefined) {
                        sph.radius = el.params.radius;
                    }
                    sph.collider = true;
                    tryApplyTRS(sph.pivot, el.transform);
                }
            } else if (el.type === 'shadowLight') {
                const data: any = el as any;
                const light = new ShadowLight();
                if (typeof data.name === 'string') light.name = data.name;
                if (typeof data.enabled === 'boolean') light.enabled = data.enabled;
                if (typeof data.intensity === 'number') light.intensity = data.intensity;
                if (Array.isArray(data.color) && data.color.length === 3) {
                    light.color = new Color(data.color[0] ?? 1, data.color[1] ?? 1, data.color[2] ?? 1);
                }
                if (typeof data.castShadows === 'boolean') light.castShadows = data.castShadows;
                if (typeof data.shadowDistance === 'number') light.shadowDistance = data.shadowDistance;
                if (typeof data.shadowResolution === 'number') light.shadowResolution = data.shadowResolution;

                await scene.add(light);
                tryApplyTRS((light as any).entity, data.transform);
                (light as any).applySettingsToComponent?.();
            }
        }

        // Apply camera snapshot (optional)
        if (manifest.camera) {
            try {
                const fp = manifest.camera.focalPoint;
                if (fp && fp.length === 3) {
                    scene.camera.setFocalPoint(new Vec3(fp[0], fp[1], fp[2]), 0);
                }
                if (typeof manifest.camera.azim === 'number' && typeof manifest.camera.elev === 'number') {
                    scene.camera.setAzimElev(manifest.camera.azim, manifest.camera.elev, 0);
                }
                if (typeof manifest.camera.distance === 'number') {
                    scene.camera.setDistance(manifest.camera.distance, 0);
                }
                if (typeof manifest.camera.fov === 'number') {
                    scene.camera.fov = manifest.camera.fov;
                }

                // Control mode + speed controls
                if (manifest.camera.controlMode === 'orbit' || manifest.camera.controlMode === 'fly' || manifest.camera.controlMode === 'walk') {
                    scene.events.fire('camera.setControlMode', manifest.camera.controlMode);
                }
                if (typeof manifest.camera.flySpeed === 'number') {
                    scene.events.fire('camera.setFlySpeed', manifest.camera.flySpeed);
                }
                if (manifest.camera.flyVerticalMode === 'keyboard' || manifest.camera.flyVerticalMode === 'free') {
                    scene.events.fire('camera.setFlyVerticalMode', manifest.camera.flyVerticalMode);
                }
            } catch (e) {
                // best-effort
            }
        }
    } finally {
        zipFs.close();
    }
}

function tryApplyTRS(entity: any, trs?: { t?: number[]; r?: number[]; s?: number[] }) {
    if (!entity || !trs) return;
    try {
        if (Array.isArray(trs.t) && trs.t.length === 3) {
            entity.setLocalPosition(new Vec3(trs.t[0], trs.t[1], trs.t[2]));
        }
        if (Array.isArray(trs.r) && trs.r.length === 4) {
            const quat = new Quat(trs.r[0], trs.r[1], trs.r[2], trs.r[3]);
            quat.normalize(); // Ensure quaternion is normalized to prevent rendering artifacts
            entity.setLocalRotation(quat);
        }
        if (Array.isArray(trs.s) && trs.s.length === 3) {
            entity.setLocalScale(new Vec3(trs.s[0], trs.s[1], trs.s[2]));
        }
    } catch (e) {
        // ignore
    }
}
